import * as VCContracts from "TFS/VersionControl/Contracts";
import * as VCSpecs from "VersionControl/Scripts/TFS.VersionControl.VersionSpecs";
import * as Utils_Date from "VSS/Utils/Date";

export function gitVersionStringToVersionDescriptor(versionString: string): VCContracts.GitVersionDescriptor {
    const result = <VCContracts.GitVersionDescriptor>{ versionOptions: VCContracts.GitVersionOptions.None };
    let versionSpec = VCSpecs.VersionSpec.parse(versionString);

    if (versionSpec instanceof VCSpecs.PreviousVersionSpec) {
        result.versionOptions = VCContracts.GitVersionOptions.PreviousChange;
        versionSpec = (<VCSpecs.PreviousVersionSpec>versionSpec).version;
    }

    if (versionSpec instanceof VCSpecs.GitBranchVersionSpec) {
        result.versionType = VCContracts.GitVersionType.Branch;
        result.version = (<VCSpecs.GitBranchVersionSpec>versionSpec).branchName;
    }
    else if (versionSpec instanceof VCSpecs.GitCommitVersionSpec) {
        result.versionType = VCContracts.GitVersionType.Commit;
        result.version = (<VCSpecs.GitCommitVersionSpec>versionSpec).commitId;
    }
    else if (versionSpec instanceof VCSpecs.GitTagVersionSpec) {
        result.versionType = VCContracts.GitVersionType.Tag;
        result.version = (<VCSpecs.GitTagVersionSpec>versionSpec).tagName;
    }

    return result;
}

export function gitVersionDescriptorToVersionSpec(versionDescriptor: VCContracts.GitVersionDescriptor): VCSpecs.VersionSpec {
    let versionSpec: VCSpecs.VersionSpec;
    switch (versionDescriptor.versionType) {
        case VCContracts.GitVersionType.Branch:
            versionSpec = new VCSpecs.GitBranchVersionSpec(versionDescriptor.version);
            break;
        case VCContracts.GitVersionType.Commit:
            versionSpec = new VCSpecs.GitBranchVersionSpec(versionDescriptor.version);
            break;
        case VCContracts.GitVersionType.Tag:
            versionSpec = new VCSpecs.GitTagVersionSpec(versionDescriptor.version);
            break;
    }
    if (versionDescriptor.versionOptions === VCContracts.GitVersionOptions.PreviousChange) {
        versionSpec = new VCSpecs.PreviousVersionSpec(versionSpec);
    }
    return versionSpec;
}

export function tfvcVersionStringToVersionDescriptor(versionString: string): VCContracts.TfvcVersionDescriptor {
    const versionSpec: VCSpecs.VersionSpec = VCSpecs.VersionSpec.parse(versionString);

    return tfvcVersionSpecToVersionDescriptor(versionSpec);
}

export function tfvcVersionSpecToVersionDescriptor(versionSpec: VCSpecs.VersionSpec): VCContracts.TfvcVersionDescriptor {
    let result: VCContracts.TfvcVersionDescriptor = <VCContracts.TfvcVersionDescriptor>{};

    if (versionSpec instanceof VCSpecs.LatestVersionSpec) {
        result.versionType = VCContracts.TfvcVersionType.Latest;
    }
    else if (versionSpec instanceof VCSpecs.TipVersionSpec) {
        result = tfvcVersionSpecToVersionDescriptor((<VCSpecs.TipVersionSpec>versionSpec).version);
        // Bug 644508: For tip version type of shelvesets server expects the first char to be prefixed ('S')
        if (versionSpec.version instanceof VCSpecs.ShelvesetVersionSpec) {
            result.version = "S" + result.version;
        }
        result.versionType = VCContracts.TfvcVersionType.Tip;
    }
    else if (versionSpec instanceof VCSpecs.PreviousVersionSpec) {
        result = tfvcVersionSpecToVersionDescriptor((<VCSpecs.PreviousVersionSpec>versionSpec).version);
        result.versionOption = VCContracts.TfvcVersionOption.Previous;
    }
    else if (versionSpec instanceof VCSpecs.ChangesetVersionSpec) {
        result.versionType = VCContracts.TfvcVersionType.Changeset;
        result.version = (<VCSpecs.ChangesetVersionSpec>versionSpec).changeset.toString();
    }
    else if (versionSpec instanceof VCSpecs.ChangeVersionSpec) {
        result.versionType = VCContracts.TfvcVersionType.Change;
        result.version = (<VCSpecs.ChangeVersionSpec>versionSpec).changeset.toString();
    }
    else if (versionSpec instanceof VCSpecs.MergeSourceVersionSpec) {
        result.versionType = VCContracts.TfvcVersionType.MergeSource;
        result.version = (<VCSpecs.MergeSourceVersionSpec>versionSpec).changeset.toString();
        result.versionOption = (<VCSpecs.MergeSourceVersionSpec>versionSpec).useRenameSource ? VCContracts.TfvcVersionOption.UseRename : VCContracts.TfvcVersionOption.None;
    }
    else if (versionSpec instanceof VCSpecs.ShelvesetVersionSpec) {
        result.versionType = VCContracts.TfvcVersionType.Shelveset;
        let shelvesetSpec: VCSpecs.ShelvesetVersionSpec = <VCSpecs.ShelvesetVersionSpec>versionSpec;
        result.version = shelvesetSpec.name + (shelvesetSpec.owner ? (";" + shelvesetSpec.owner) : "");
    }
    else if (versionSpec instanceof VCSpecs.DateVersionSpec) {
        result.versionType = VCContracts.TfvcVersionType.Date;
        result.version = (<VCSpecs.DateVersionSpec>versionSpec).date;
    }
    else {
        result.versionType = VCContracts.TfvcVersionType.Latest;
    }

    return result;
}

export function tfvcVersionDescriptorToVersionSpec(versionDescriptor: VCContracts.TfvcVersionDescriptor): VCSpecs.VersionSpec {
    let versionSpec: VCSpecs.VersionSpec;
    switch (versionDescriptor.versionType) {
        case VCContracts.TfvcVersionType.Change:
            versionSpec = new VCSpecs.ChangeVersionSpec(parseInt(versionDescriptor.version, 10));
            break;
        case VCContracts.TfvcVersionType.Changeset:
            versionSpec = new VCSpecs.ChangesetVersionSpec(parseInt(versionDescriptor.version, 10));
            break;
        case VCContracts.TfvcVersionType.Date:
            var dateVal = Utils_Date.parseDateString(versionDescriptor.version);
            versionSpec = new VCSpecs.DateVersionSpec(dateVal);
            break;
        case VCContracts.TfvcVersionType.MergeSource:
            versionSpec = new VCSpecs.MergeSourceVersionSpec(
                parseInt(versionDescriptor.version),
                versionDescriptor.versionOption === VCContracts.TfvcVersionOption.UseRename);
            break;
        case VCContracts.TfvcVersionType.Shelveset:
            var parts = versionDescriptor.version.split(";");
            versionSpec = new VCSpecs.ShelvesetVersionSpec(parts[0], parts[1]);
            break;
        case VCContracts.TfvcVersionType.Tip:
            if (versionDescriptor.version[0] === "S") {
                versionSpec = VCSpecs.VersionSpec.parse(versionDescriptor.version.substring(1));
            } else {
                var changeSet = parseInt(versionDescriptor.version);
                versionSpec = new VCSpecs.ChangesetVersionSpec(changeSet);
            }
            versionSpec = new VCSpecs.TipVersionSpec(versionSpec);
        case VCContracts.TfvcVersionType.Latest:
        default:
            versionSpec = new VCSpecs.LatestVersionSpec();
            break;
    }
    return versionSpec;
}

export function getBranchFullName(versionSpec: VCSpecs.VersionSpec): string {
    if (versionSpec instanceof VCSpecs.GitBranchVersionSpec) {
        return versionSpec.toFullName();
    }
}
